import { Button, Form, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";

interface IProps {
  onChange?: (value: SubcategoryForm[]) => void;
  value?: SubcategoryForm[];
}

export interface SubcategoryForm {
  nameRu: string;
  nameKy: string;
  isEditing?: boolean;
}

const SelectProductCategory = (props: IProps) => {
  const { t } = useTranslation();
  const [currentForm, setCurrentForm] = useState<SubcategoryForm>({ nameRu: "", nameKy: "" });
  const [savedForms, setSavedForms] = useState<SubcategoryForm[]>(
    props.value?.map(item => ({ ...item, isEditing: false })) || []
  );

  useEffect(() => {
    if (props.value) {
      setSavedForms(props.value.map(item => ({ ...item, isEditing: false })));
    }
  }, [props.value]);

  const notifyChange = (newList: SubcategoryForm[]) => {
    setSavedForms(newList);
    const cleanList = newList.map(({ isEditing, ...item }) => item);
    props.onChange?.(cleanList);
  };

  const handleCurrentFormChange = (field: keyof SubcategoryForm, value: string) => {
    setCurrentForm(prev => ({ ...prev, [field]: value }));
  };

  const addSubcategory = () => {
    if (currentForm.nameRu.trim() && currentForm.nameKy.trim()) {
      notifyChange([{ ...currentForm, isEditing: false }, ...savedForms]);
      setCurrentForm({ nameRu: "", nameKy: "" });
    }
  };

  const removeSubcategory = (index: number) => {
    notifyChange(savedForms.filter((_, i) => i !== index));
  };

  const toggleEdit = (index: number) => {
    const newForms = savedForms.map((form, i) => 
      i === index ? { ...form, isEditing: !form.isEditing } : { ...form, isEditing: false }
    );
    setSavedForms(newForms);
  };

  const handleSavedFormChange = (index: number, field: keyof SubcategoryForm, value: string) => {
    const newForms = savedForms.map((form, i) => 
      i === index ? { ...form, [field]: value } : form
    );
    setSavedForms(newForms);
  };

  const saveChanges = (index: number) => {
    const newForms = savedForms.map((form, i) => 
      i === index ? { ...form, isEditing: false } : form
    );
    notifyChange(newForms);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h4 style={{ margin: '0 0 8px 0' }}>{t('handbooks.types_of_products.Subcategories')}</h4>
        <Space style={{ width: '100%', display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 8, flex: 1 }}>
            <Form.Item
              style={{ margin: 0, flex: 1 }}
              rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
            >
              <Input
                placeholder={t("general.nameRu")}
                value={currentForm.nameRu}
                onChange={(e) => handleCurrentFormChange("nameRu", e.target.value)}
              />
            </Form.Item>

            <Form.Item
              style={{ margin: 0, flex: 1 }}
              rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
            >
              <Input
                placeholder={t("general.nameKy")}
                value={currentForm.nameKy}
                onChange={(e) => handleCurrentFormChange("nameKy", e.target.value)}
              />
            </Form.Item>
          </div>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addSubcategory}
            disabled={!currentForm.nameRu.trim() || !currentForm.nameKy.trim()}
          />
        </Space>
      </div>

      <div 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 8,
          maxHeight: '300px',
          overflowY: savedForms.length > 5 ? 'auto' : 'visible',
          paddingRight: savedForms.length > 5 ? '8px' : 0
        }}
      >
        {savedForms.map((form, index) => (
          <Space key={index} style={{ width: '100%', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 8, flex: 1 }}>
              <Form.Item style={{ margin: 0, flex: 1 }}>
                <Input
                  value={form.nameRu}
                  onChange={(e) => handleSavedFormChange(index, "nameRu", e.target.value)}
                  disabled={!form.isEditing}
                />
              </Form.Item>

              <Form.Item style={{ margin: 0, flex: 1 }}>
                <Input
                  value={form.nameKy}
                  onChange={(e) => handleSavedFormChange(index, "nameKy", e.target.value)}
                  disabled={!form.isEditing}
                />
              </Form.Item>
            </div>

            <Space>
              <Button
                type={form.isEditing ? "primary" : "text"}
                icon={form.isEditing ? <SaveOutlined /> : <EditOutlined />}
                onClick={() => form.isEditing ? saveChanges(index) : toggleEdit(index)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeSubcategory(index)}
              />
            </Space>
          </Space>
        ))}
      </div>
    </div>
  );
};

export default SelectProductCategory;